"use client";

import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/alert/Alert";
import { Button } from "@/components/ui/button/Button";
import SelectDropdown from "@/components/ui/select-dropdown";
import { TEXT_BODY } from "@/components/ui/surface/surfaceTokens";

type BindingKind = "ani_artifact" | "plannr_submission";

export type NarrativePreviewBinding = {
  kind: BindingKind;
  artifact_id?: string;
  form_uuid?: string;
  submission_uuid?: string;
};

type NarrativeBinding = NarrativePreviewBinding & {
  scope?: string;
  label?: string;
  form_title?: string;
};

type Candidate = Record<string, unknown>;

export type NarrativeSourcePanelData = {
  effective?: NarrativeBinding | null;
  household?: NarrativeBinding | null;
  mission_override?: NarrativeBinding | null;
  candidates?: { plannr?: Candidate[]; ani?: Candidate[] };
  has_mission?: boolean;
  canonical_source?: string;
  plannr_form_title?: string;
};

function bindingKey(kind: BindingKind, row: Candidate): string {
  if (kind === "ani_artifact") return `ani:${String(row.artifact_id ?? "")}`;
  return `plannr:${String(row.form_uuid ?? "")}:${String(row.submission_uuid ?? "")}`;
}

function labelForRow(kind: BindingKind, row: Candidate): string {
  if (kind === "ani_artifact") {
    const title = String(row.label ?? "Draft narrative");
    const version = row.version != null ? ` v${row.version}` : "";
    const approved = row.approved ? " · approved" : "";
    return `${title}${version}${approved}`;
  }
  const title = String(row.form_title ?? "Narrative form");
  const stage = String(row.stage ?? row.status ?? "").trim();
  const submitted = row.submitted ? "submitted" : "draft";
  return stage ? `${title} · ${stage}` : `${title} · ${submitted}`;
}

function previewBindingFromKey(
  selectedKey: string,
  options: { key: string; kind: BindingKind; row: Candidate }[],
): NarrativePreviewBinding | null {
  if (!selectedKey) return null;
  const picked = options.find((opt) => opt.key === selectedKey);
  if (!picked) return null;
  if (picked.kind === "ani_artifact") {
    const artifact_id = String(picked.row.artifact_id ?? "").trim();
    return artifact_id ? { kind: "ani_artifact", artifact_id } : null;
  }
  const form_uuid = String(picked.row.form_uuid ?? "").trim();
  const submission_uuid = String(picked.row.submission_uuid ?? "").trim();
  return form_uuid && submission_uuid ? { kind: "plannr_submission", form_uuid, submission_uuid } : null;
}

export function NarrativeSourcePicker({
  panel,
  className,
  onSave,
  onPreviewBindingChange,
  saving = false,
}: {
  panel: NarrativeSourcePanelData;
  className?: string;
  onSave: (values: Record<string, unknown>) => Promise<void>;
  /** Fires when the adviser picks a concrete source (not Automatic). */
  onPreviewBindingChange?: (binding: NarrativePreviewBinding | null) => void;
  saving?: boolean;
}) {
  const plannr = panel.candidates?.plannr ?? [];
  const ani = panel.candidates?.ani ?? [];
  const hasMission = Boolean(panel.has_mission);

  const options = useMemo(() => {
    const rows: { key: string; kind: BindingKind; row: Candidate; label: string }[] = [];
    for (const row of ani) {
      rows.push({ key: bindingKey("ani_artifact", row), kind: "ani_artifact", row, label: labelForRow("ani_artifact", row) });
    }
    for (const row of plannr) {
      rows.push({ key: bindingKey("plannr_submission", row), kind: "plannr_submission", row, label: labelForRow("plannr_submission", row) });
    }
    return rows;
  }, [ani, plannr]);

  const effectiveKey = useMemo(() => {
    const effective = panel.effective;
    if (!effective) return "";
    if (effective.kind === "ani_artifact") return bindingKey("ani_artifact", effective);
    return bindingKey("plannr_submission", effective);
  }, [panel.effective]);

  const [selectedKey, setSelectedKey] = useState(effectiveKey);
  const [scope, setScope] = useState<"household" | "mission">(
    panel.effective?.scope === "mission" ? "mission" : "household",
  );

  useEffect(() => {
    setSelectedKey(effectiveKey);
    setScope(panel.effective?.scope === "mission" ? "mission" : "household");
  }, [effectiveKey, panel.effective?.scope]);

  useEffect(() => {
    onPreviewBindingChange?.(previewBindingFromKey(selectedKey, options));
  }, [onPreviewBindingChange, options, selectedKey]);

  const handleSave = useCallback(async () => {
    if (!selectedKey) {
      await onSave({ kind: "clear", scope });
      return;
    }
    const picked = options.find((opt) => opt.key === selectedKey);
    if (!picked) return;
    if (picked.kind === "ani_artifact") {
      await onSave({
        kind: "ani_artifact",
        artifact_id: String(picked.row.artifact_id ?? ""),
        scope,
      });
      return;
    }
    await onSave({
      kind: "plannr_submission",
      form_uuid: String(picked.row.form_uuid ?? ""),
      submission_uuid: String(picked.row.submission_uuid ?? ""),
      scope,
    });
  }, [onSave, options, scope, selectedKey]);

  if (options.length === 0 && !panel.effective) {
    return null;
  }

  return (
    <div className={clsx("rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-800/40", className)} data-testid="narrative-source-picker">
      <p className={clsx("text-sm font-medium text-slate-900 dark:text-slate-100")}>Source of truth for this case</p>
      <p className={clsx("mt-1 text-xs text-slate-600 dark:text-slate-400", TEXT_BODY)}>
        Letter, advice pack, and charts use this when set. Leave on automatic to keep today&apos;s heuristics.
      </p>

      {panel.canonical_source === "plannr" && panel.plannr_form_title ? (
        <Alert variant="info" borderless className="mt-3 !p-2.5 text-sm">
          Canonical source is Plannr: <span className="font-medium">{panel.plannr_form_title}</span>. Draft or edit the Ani narrative separately if needed.
        </Alert>
      ) : null}

      <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto">
        <li>
          <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-white/80 dark:hover:bg-slate-900/40">
            <input type="radio" name="narrative-source" checked={!selectedKey} onChange={() => setSelectedKey("")} className="mt-1" />
            <span className="text-slate-600 dark:text-slate-300">Automatic (no saved preference)</span>
          </label>
        </li>
        {options.map((opt) => (
          <li key={opt.key}>
            <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-white/80 dark:hover:bg-slate-900/40">
              <input
                type="radio"
                name="narrative-source"
                checked={selectedKey === opt.key}
                onChange={() => setSelectedKey(opt.key)}
                className="mt-1"
              />
              <span className="min-w-0 text-slate-900 dark:text-slate-100">{opt.label}</span>
            </label>
          </li>
        ))}
      </ul>

      {hasMission ? (
        <div className="mt-3 max-w-xs">
          <SelectDropdown
            label="Save preference for"
            value={scope}
            onChange={(value) => setScope(value as "household" | "mission")}
            options={[
              { value: "household", label: "This client (all tasks)" },
              { value: "mission", label: "This mission only" },
            ]}
          />
        </div>
      ) : null}

      <div className="mt-3 flex justify-end">
        <Button type="button" size="sm" color="indigo" onClick={() => void handleSave()} disabled={saving} data-testid="narrative-source-save">
          {saving ? "Saving…" : "Save source"}
        </Button>
      </div>
    </div>
  );
}
